import type { CardView, FundView, OfferView, PageView } from "@highmarch/app";
import { BalanceData, OfferItem, offerOf } from "@highmarch/domain";
import { checkHtml, flagHtml, kickerHtml, secHtml, timerChipHtml } from "../components";
import { uiUrl } from "../portraits";
import {
  cardRuleText, claimKicker, dayText, fundGems, fundRowFill, fundStops,
} from "./cardsModel";
import { TileNames, fmtCountdown, itemTile, itemTiles, priceText } from "./dealsModel";
import { countdownHtml, priceBtnHtml, valueBurstHtml, wellOf, wellsOf } from "./dealsTiles";

/**
 * The Cards and War Chest Fund pages (MP-01/02/03) as markup, in the Deals pages' language (dealsPages.ts): cream offer
 * cards with a title flag, wells, the one value burst and the gold price with its VIP XP bubble; a running card shows
 * its day, its countdown and a gold CLAIM; the fund is a milestone track like the Weekly Deal's token track. Every word
 * comes from cardsModel.ts; every rule from @highmarch/app `Cards`.
 */

/** What the Cards pages need beyond a Deals page's context. */
export interface CardsCtx {
  readonly cards: readonly CardView[];
  readonly fund: FundView | null;
}

/** The parts of the Deals page context these pages read (dealsPages.ts PageCtx). */
export interface CardsPageCtx {
  readonly balance: BalanceData;
  readonly level: number;
  readonly now: number;
  readonly prices: ReadonlyMap<string, string>;
  readonly names: TileNames;
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);
/** The fund's track wraps after this many stops, so each keeps a full-size well at 375 px (the Weekly track holds 5). */
const FUND_STOPS_PER_ROW = 5;

/** The Cards or Fund page body; "" for any other page. */
export function cardsBodyHtml(p: PageView, c: CardsPageCtx, k: CardsCtx): string {
  if (p.page.id === "fund") return k.fund ? fundBodyHtml(p, c, k.fund) : "";
  return p.offers.map((o) => { const v = k.cards.find((x) => x.offerId === o.offerId); return v ? cardHtml(o, v, c) : ""; }).join("");
}

function burst(o: OfferView): string { return o.showValue ? valueBurstHtml(o.valuePercent) : ""; }

function price(o: OfferView, c: CardsPageCtx, size: "card" | "main" = "card"): string {
  return priceBtnHtml({ offerId: o.offerId, label: priceText(o.price, c.prices), vipPoints: o.vipPoints, blocked: "", bubbleLeft: size === "card" }, size);
}

function wells(items: readonly OfferItem[], c: CardsPageCtx): string { return wellsOf(itemTiles(c.balance, items, c.level, [], c.names)); }

/**
 * One card: on sale (what it pays now and each day, the price), or running (its day, CLAIM or the next day's
 * countdown; in its last days the price that extends it, once nothing waits to be claimed: one gold button a card).
 */
function cardHtml(o: OfferView, v: CardView, c: CardsPageCtx): string {
  const now = offerOf(c.balance.offers, o.offerId)?.items ?? [];
  const cls = `k-card doffer dcard${v.running ? " on" : ""}${v.claimable > 0 ? " ready" : ""}`;
  if (!v.running) {
    return `<article class="${cls}" data-offer="${o.offerId}">${flagHtml(esc(o.name))}${burst(o)}
      <div class="drow"><div class="dgrid"><div class="cparts">
        <div class="cpart">${kickerHtml("Now")}<div class="dwells">${wells(now, c)}</div></div>
        <div class="cpart grow">${kickerHtml(`Daily · ${v.days} days`)}<div class="dwells">${wells(v.daily, c)}</div></div></div>
        </div>
      <div class="dpay">${price(o, c)}</div></div></article>`;
  }
  const items = v.claimable > 0 ? v.claimItems : v.daily;
  const right = v.claimable > 0 ? `<button type="button" class="btn gold dcclaim disp" data-card="${o.offerId}">CLAIM</button>`
    : `<span class="cnext">${kickerHtml("Next day in")}${timerChipHtml(fmtCountdown(v.nextAt - c.now), { endsAt: v.nextAt })}</span>`;
  return `<article class="${cls}" data-offer="${o.offerId}">${flagHtml(esc(o.name))}${burst(o)}
    <div class="cstat"><b class="disp">${dayText(v)}</b>${v.endsAt !== null ? countdownHtml(v.endsAt, fmtCountdown(v.endsAt - c.now), c.now, "Ends in") : ""}</div>
    <div class="drow"><div class="dgrid">${kickerHtml(v.claimable > 0 ? claimKicker(v) : "Tomorrow")}<div class="dwells">${wells(items, c)}</div></div>
    <div class="dpay">${right}${v.canBuy && v.claimable === 0 ? `${price(o, c)}<small class="rem">Adds ${v.days} days</small>` : ""}</div></div>
    <small class="cnote">${esc(cardRuleText(v))}</small></article>`;
}

/** The fund: its milestone track (two rows of five), then the offer (big gems and the price) or what is owned. */
function fundBodyHtml(p: PageView, c: CardsPageCtx, f: FundView): string {
  const o = p.offers[0];
  const stops = fundStops(f), { total, paid } = fundGems(f);
  const rows: string[] = [];
  for (let i = 0; i < stops.length; i += FUND_STOPS_PER_ROW) {
    const row = stops.slice(i, i + FUND_STOPS_PER_ROW), n = row.length;
    const from = i === 0 ? p.page.unlockAfterLevel : stops[i - 1].level;
    const fill = fundRowFill(row.map((s) => s.level), from, f.cleared);
    const cols = row.map((s) => {
      const tile = s.items[0] ? itemTile(c.balance, s.items[0], c.level) : null;
      const what = s.state === "claimed" ? " · claimed" : s.state === "ready" ? " · claim now" : "";
      return `<button type="button" class="tstop fstop s-${s.state}" data-level="${s.level}" aria-label="Level ${s.level}: ${esc(tile?.name ?? "")}${what}">`
        + `<span class="trew">${tile ? wellOf(tile, "sm", "span", s.state === "claimed" ? "dim" : "") : ""}${s.state === "claimed" ? checkHtml("tcheck") : ""}</span><b class="tn">L${s.level}</b></button>`;
    }).join("");
    const pips = row.map((s, j) => `<i class="pip${s.state !== "locked" ? " on" : ""}" style="--x:${((j + 0.5) / n).toFixed(4)}"></i>`).join("");
    const w = Math.max(0, Math.min(1, fill)) * n, bar = w < 1 ? (w * 0.5) / n : (w - 0.5) / n;
    rows.push(`<div class="tstops" style="--n:${n}">${cols}</div><div class="bar tbar"><div class="fill gold" style="width:${(bar * 100).toFixed(2)}%"></div>${pips}</div>`);
  }
  const track = `<section class="k-card plate dtrack dfund"><div class="thead"><img decoding="async" src="${uiUrl("icons/gem_pack_3@3x.png")}" alt="">`
    + `<b class="disp">${(f.owned ? paid : total).toLocaleString("en-US")}</b><span class="tlbl">${f.owned ? `of ${total.toLocaleString("en-US")} gems` : "gems"}</span></div>${rows.join("")}</section>`;
  if (!o) return track;
  const offer = f.owned
    ? `<article class="k-card doffer fowned" data-offer="${o.offerId}">${flagHtml(esc(o.name), "navy")}<p class="fown">${checkHtml("fchk")}<span>Owned</span></p></article>`
    : `<article class="k-card hl doffer hero" data-offer="${o.offerId}">${burst(o)}
      <div class="hgems"><img decoding="async" src="${uiUrl("icons/gem_pack_3@3x.png")}" alt=""><b class="disp">${total.toLocaleString("en-US")}</b><small>gems</small></div>
      ${price(o, c, "main")}</article>`;
  return `${track}${secHtml(f.owned ? "Owned" : "War Chest Fund")}${offer}`;
}
